import { useState } from "react";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, ShieldCheck, ShieldAlert, ShieldX, Copy, RefreshCw,
  UserCheck, AlertOctagon, Info, PhoneOff, Send,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


const fmtPhone = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return raw;
};
const _daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};


function Badge({ tone = "slate", icon: Icon, children, testid }) {
  const tones = {
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    slate: "text-slate-400 border-slate-700 bg-slate-800/30",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border ${tones[tone] || tones.slate}`}>
      {Icon && <Icon className="h-3 w-3" />} {children}
    </span>
  );
}


function CopyBtn({ value, label = "Copy" }) {
  if (!value) return null;
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }}
      className="text-slate-500 hover:text-emerald-300"
      title={label}
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}


export default function OwnerContactIntelligencePanel({ opp, onUpdate }) {
  const [busy, setBusy] = useState(false);
  if (!opp) return null;
  const asset = (opp.asset_type || "").toLowerCase();
  if (asset !== "multifamily" && asset !== "land") return null;

  const status = opp.batchdata_enrichment_status || "Not Run";
  const daysOld = _daysSince(opp.batchdata_last_enriched);
  const stale = daysOld !== null && daysOld > 90;

  const runSkipTrace = async (force = false) => {
    if (force && !window.confirm("This will use 1 BatchData credit ($0.02). Continue?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/batchdata/skip-trace${force ? "?force=true" : ""}`);
      const res = r.data?.result || {};
      if (res.status === "success") toast.success("Owner contact enriched");
      else if (res.status === "cached") toast.info("Cache still fresh ,  no API call made");
      else if (res.status === "no_match") toast.warning("No contact found ,  manual research task created");
      else if (res.status === "blocked") toast.error(`Blocked: ${res.reason}`);
      onUpdate?.(r.data.opp);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "object" && d?.reason) ? `Blocked by gate ${d.reason}` : (d || "Skip trace failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // States
  if (status === "Not Run") {
    return (
      <div className="ae-card p-5 space-y-3" data-testid="owner-contact-panel">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <span>Owner Contact Intelligence</span>
          <span title="BatchData-powered skip trace with TCPA/DNC/litigator scrub. Requires cf_readiness_score >= 55. Never returns non-TCPA-safe numbers. LLC piercing identifies the real person behind the entity." className="cursor-help text-slate-600 hover:text-emerald-300"><Info className="h-3 w-3" /></span>
        </div>
        <div className="text-sm text-slate-500">Owner contact not retrieved.</div>
        <button
          onClick={() => runSkipTrace(false)} disabled={busy}
          data-testid="owner-contact-get-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          <UserCheck className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Retrieving…" : "Get Contact Info →"}
        </button>
      </div>
    );
  }

  if (status === "No Match") {
    return (
      <div className="ae-card p-5 space-y-3" data-testid="owner-contact-panel">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Owner Contact Intelligence</div>
        <div className="text-sm text-slate-400">
          No contact found for this owner. Manual research or direct mail recommended.
        </div>
        <button
          onClick={() => runSkipTrace(true)} disabled={busy}
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          Retry Skip Trace
        </button>
      </div>
    );
  }

  const conf = opp.bd_match_confidence;
  const pierced = opp.bd_llc_pierced;
  const displayName = pierced ? opp.bd_true_owner_name : opp.bd_owner_full_name;
  const blocked = !!opp.outreach_blocked;
  const dnc = !!opp.bd_dnc_flag;

  const phones = [
    { num: opp.bd_phone_1, type: opp.bd_phone_1_type, safe: opp.bd_phone_1_tcpa_safe },
    { num: opp.bd_phone_2, type: opp.bd_phone_2_type, safe: opp.bd_phone_2_tcpa_safe },
    { num: opp.bd_phone_3, type: null, safe: opp.bd_phone_3_tcpa_safe },
  ].filter((p) => p.num);

  return (
    <div className="ae-card p-5 space-y-5" data-testid="owner-contact-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
            <span>Owner Contact Intelligence</span>
            <span title="BatchData skip trace. Phones ranked by confidence; only TCPA-safe numbers surface to owner_phone. LLC piercing identifies the true owner." className="cursor-help text-slate-600"><Info className="h-3 w-3" /></span>
          </div>
          {typeof conf === "number" && (
            <Badge tone={conf >= 85 ? "emerald" : conf >= 60 ? "amber" : "slate"} testid="bd-match-conf">
              {conf}% confidence
            </Badge>
          )}
        </div>
      </div>

      {/* Row 1 ,  Owner identity */}
      <div className="space-y-1" data-testid="owner-identity">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-base font-semibold text-slate-100">{displayName || "Unknown"}</div>
          {pierced ? (
            <Badge tone="emerald" icon={UserCheck} testid="llc-pierced-badge">LLC Pierced</Badge>
          ) : (
            opp.bd_owner_type && opp.bd_owner_type !== "Unknown" && (
              <Badge tone="slate">{opp.bd_owner_type}</Badge>
            )
          )}
          {stale && <Badge tone="amber">Data may be stale ,  refresh recommended</Badge>}
        </div>
        {pierced && (
          <div className="text-[11px] text-slate-500">
            True owner identified behind {opp.bd_owner_full_name}
          </div>
        )}
      </div>

      {/* Row 2 ,  Contact cards */}
      <div className="grid md:grid-cols-3 gap-3">
        {/* Phone */}
        <div className="ae-card p-4 space-y-2" data-testid="bd-phone-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Phone
          </div>
          {phones.length === 0 ? (
            <div className="text-xs text-slate-500">No phones found</div>
          ) : (
            <>
              {phones.map((p, i) => (
                <div key={i} className={i === 0 ? "space-y-1" : "space-y-1 pt-2 border-t border-white/8 text-xs"}>
                  <div className="flex items-center gap-2">
                    <div className={i === 0 ? "font-mono text-sm text-slate-100" : "font-mono text-xs text-slate-300"}>
                      {fmtPhone(p.num)}
                    </div>
                    <CopyBtn value={p.num} label="Phone" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {p.type && <Badge tone="slate">{p.type}</Badge>}
                    {p.safe && <Badge tone="emerald" icon={ShieldCheck}>TCPA Safe</Badge>}
                    {/* DNC/SMS badge suppressed ,  SMS outreach disabled platform-wide */}
                    {i === 0 && blocked && <Badge tone="rose" icon={ShieldX} testid="tcpa-risk-badge">TCPA Risk ,  Outreach Blocked</Badge>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Email */}
        <div className="ae-card p-4 space-y-2" data-testid="bd-email-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Mail className="h-3 w-3" /> Email
          </div>
          {opp.bd_email_1 ? (
            <>
              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-100 truncate">{opp.bd_email_1}</div>
                <CopyBtn value={opp.bd_email_1} label="Email" />
              </div>
              <Badge tone={opp.bd_email_1_deliverable ? "emerald" : "rose"} icon={opp.bd_email_1_deliverable ? ShieldCheck : ShieldX}>
                {opp.bd_email_1_deliverable ? "Deliverable" : "Undeliverable"}
              </Badge>
              {opp.bd_email_2 && (
                <div className="text-xs text-slate-400 truncate pt-2 border-t border-white/8">{opp.bd_email_2}</div>
              )}
            </>
          ) : (
            <div className="text-xs text-slate-500">No email found</div>
          )}
        </div>

        {/* Mailing */}
        <div className="ae-card p-4 space-y-2" data-testid="bd-mail-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> Mailing
          </div>
          {opp.bd_mailing_address_confirmed ? (
            <>
              <div className="text-sm text-slate-100">{opp.bd_mailing_address_confirmed}</div>
              <div className="text-xs text-slate-400">
                {[opp.bd_mailing_city, opp.bd_mailing_state, opp.bd_mailing_zip].filter(Boolean).join(", ")}
              </div>
              {opp.absentee_owner_flag && <Badge tone="amber">Absentee Confirmed</Badge>}
            </>
          ) : (
            <div className="text-xs text-slate-500">No mailing address found</div>
          )}
        </div>
      </div>

      {/* Row 3 ,  Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          disabled={blocked}
          data-testid="owner-contact-outreach-btn"
          title={blocked ? "Outreach blocked ,  TCPA litigator flagged. Contact support." : "Start outreach sequence"}
          className={`text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border ${
            blocked
              ? "border-rose-500/30 text-rose-400 cursor-not-allowed opacity-70"
              : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          }`}
        >
          {blocked ? <AlertOctagon className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
          {blocked ? "Outreach Blocked" : "Start Outreach"}
        </button>
        <button
          onClick={() => runSkipTrace(true)} disabled={busy}
          data-testid="owner-contact-refresh-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-300 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          Refresh Contact
        </button>
        {dnc && !blocked && (
          {/* SMS-suppressed badge hidden ,  SMS outreach disabled platform-wide */}
        )}
      </div>

      {/* Freshness footer */}
      {opp.batchdata_last_enriched && (
        <div className="text-[10px] text-slate-600 pt-2 border-t border-white/8">
          Contact data as of {fmtDateTime(opp.batchdata_last_enriched)}
          {daysOld !== null && ` (${daysOld}d ago)`}
        </div>
      )}
    </div>
  );
}
